import React, { useEffect, useState } from 'react';
import axios from 'axios';
import css from './DashboardListingWrapper.module.css';
import classnames from 'classnames';

import { withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';

import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';

import config from '../../../config';
import UpgradeRequiredModal from '../../elements/upgrade-required-modal/UpgradeRequiredModal';

import {
  faEdit,
  faShare,
  faTrash,
  faLock,
  faLockOpen,
} from '@fortawesome/free-solid-svg-icons';

import { types as sdkTypes } from '../../../util/sdkLoader';

import IconSpinner from '../../../components/IconSpinner/IconSpinner';

import DefaultListing from '../../../assets/default-listing.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { closeNotification, showNotification } from '../../../custom-ducks/utility.duck';
import { getAllCoupons } from '../../../custom-ducks/coupons.duck';
import { strToSlug } from '../../../util/seo';
import NewListingCard from '../../elements/new-listing-card/NewListingCard';
import { brandedConfirmAlert } from '../../../components/PopupShell/brandedConfirmAlert';
import { Plus, Tag } from 'react-feather';
import CreateCouponPopup from './CreateCouponPopup/CreateCouponPopup';
import { decode } from 'html-entities';
import { notifyError } from '../../../components/HotToast/HotToastProvider';

// Mirrors the per-panel validateFields() checks in the add-listing wizard
// (Overview/Services/Gallery) — read straight off the listing entity so a
// closed ad that skipped a tab (or lost data via an old draft) can't be
// reactivated with required fields missing. Ordered to match the wizard's
// tab order, so the coach is sent to fix things top-down.
const REQUIRED_FIELD_CHECKS = [
  {
    tab: 'overview',
    field: 'title',
    label: 'a listing title (at least 10 characters)',
    isMissing: item => !(item.attributes.title && item.attributes.title.length >= 10),
  },
  {
    tab: 'overview',
    field: 'program',
    label: 'a linked program',
    isMissing: item => !(item.attributes.publicData || {}).programId,
  },
  {
    tab: 'overview',
    field: 'description',
    label: 'a description (at least 500 characters)',
    isMissing: item => {
      const text = decode(item.attributes.description || '').replace(/<[^>]+>/g, '');
      return text.length <= 500;
    },
  },
  {
    tab: 'overview',
    field: 'language',
    label: 'at least 1 language',
    isMissing: item => {
      const language = (item.attributes.publicData || {}).language;
      return !Array.isArray(language) || language.length < 1;
    },
  },
  {
    tab: 'overview',
    field: 'location',
    label: 'a location',
    isMissing: item => {
      const location = (item.attributes.publicData || {}).location;
      return !(location && location.address && location.address.length > 0);
    },
  },
  {
    tab: 'service',
    field: 'price',
    label: 'a price',
    isMissing: item => !(item.attributes.price && item.attributes.price.amount > 0),
  },
  {
    tab: 'service',
    field: 'typeOfService',
    label: 'a type of service',
    isMissing: item => {
      const typeOfService = (item.attributes.publicData || {})['type-of-service'];
      return !Array.isArray(typeOfService) || typeOfService.length < 1;
    },
  },
  {
    tab: 'service',
    field: 'meetingType',
    label: 'a meeting type',
    isMissing: item => {
      const meetingType = (item.attributes.publicData || {})['meeting-type'];
      return !Array.isArray(meetingType) || meetingType.length < 1;
    },
  },
  {
    tab: 'service',
    field: 'service',
    label: 'who you deliver the service to',
    isMissing: item => {
      const service = (item.attributes.publicData || {})['service-delivery-to'];
      return !Array.isArray(service) || service.length < 1;
    },
  },
  {
    tab: 'gallery',
    field: 'images',
    label: 'at least 1 photo',
    isMissing: item => !Array.isArray(item.images) || item.images.length < 1,
  },
];

const getMissingRequiredField = item =>
  REQUIRED_FIELD_CHECKS.find(check => check.isMissing(item)) || null;

// Mirrors listing-service/scripts/limits.js TIER_NAMES — display labels
// for profile.metadata.subscriptionDetails.subscription (0/1/2), used to
// populate UpgradeRequiredModal copy without a round trip to the backend
// for the no_subscription / subscription_expired gates.
const SUBSCRIPTION_TIER_NAMES = {
  0: null,
  1: 'Network Member',
  2: 'Verified Coach',
};

function DashboardListingWrapper(props) {
  const {
    listings,
    onCloseListing,
    onOpenListing,
    onDiscardListing,
    currentUser,
    queryInProgress,
    notify,
    errorsToShow,
    coupons,
    couponsFetching,
    onGetAllCoupons,
  } = props;

  useEffect(() => {
    onGetAllCoupons();
  }, []);

  useEffect(() => {
    console.log({ errorsToShow });

    if (errorsToShow.length > 0) {
      brandedConfirmAlert({
        title: 'You have some warnings to fix',
        message: errorsToShow.join(', '),
        tone: 'warning',
        buttons: [
          {
            label: 'Ok',
          },
        ],
      });
    }
  }, [errorsToShow]);

  const { UUID } = sdkTypes;

  const currentProfile = currentUser && currentUser.attributes.profile;

  const noImage = DefaultListing;

  const getListings = state => {
    if (listings.length > 0) {
      return listings.filter(item => {
        return item.attributes.state === state;
      });
    } else {
      return [];
    }
  };

  const getDescription = text => {
    const length = 100;
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const decoded = doc.documentElement.textContent;
    const clean = decoded.replace(/<[^>]*>/g, '');
    return clean.length <= length ? clean : clean.substring(0, length) + '...';
  };

  const historuPush = path => {
    props.history.push(path);
  };

  const intl = useIntl();

  const [upgradeDecision, setUpgradeDecision] = useState(null);
  // Drives a spinner on both "Create Ad" entry points while the
  // listing-precheck request is in flight — without it the click looked
  // dead for however long that network round trip took before the new
  // page appeared.
  const [isCreatingAd, setIsCreatingAd] = useState(false);

  const closeUpgradeModal = () => setUpgradeDecision(null);
  const handleUpgradeClick = () => {
    setUpgradeDecision(null);
    props.history.push('/account/payments/pricing');
  };

  const onAddListingClick = async () => {
    if (isCreatingAd) return;
    setIsCreatingAd(true);

    try {
      // Subscription is a hard gate, checked before anything else, and
      // shown via the dedicated UpgradeRequiredModal (not the generic
      // "complete your profile" checklist below) — see
      // SUBSCRIPTION-GATING-SPEC. `currentProfile.metadata` is null for
      // any coach who has never had a subscription, so guard it before
      // reading `.subscriptionDetails` off it (this used to throw and
      // silently abort the whole click handler for exactly that coach).
      const isMetaAvailable = !!(currentProfile && currentProfile.metadata);
      const subscriptionDetails = isMetaAvailable ? currentProfile.metadata.subscriptionDetails : null;
      const tier =
        subscriptionDetails && typeof subscriptionDetails.subscription === 'number'
          ? subscriptionDetails.subscription
          : 0;

      if (tier <= 0) {
        setUpgradeDecision({
          reason: 'no_subscription',
          tier: 0,
          tierName: SUBSCRIPTION_TIER_NAMES[0],
          requiredTier: 1,
          requiredTierName: SUBSCRIPTION_TIER_NAMES[1],
        });
        return;
      }

      if (
        subscriptionDetails.expiryDate &&
        parseInt(subscriptionDetails.expiryDate) < new Date().getTime()
      ) {
        setUpgradeDecision({
          reason: 'subscription_expired',
          tier,
          tierName: SUBSCRIPTION_TIER_NAMES[tier],
          requiredTier: tier,
          requiredTierName: SUBSCRIPTION_TIER_NAMES[tier],
        });
        return;
      }

      // Stripe connection + profile completeness (display name, phone,
      // photo, bio) are no longer gated here at "Create Ad" time — the
      // coach can start the wizard immediately after clearing the
      // subscription gate above. Those checks now run at Submit time in
      // Gallery.js, after the ad is saved as a draft, via
      // CompleteProfileModal. See Gallery.js's getMissingProfileItems /
      // onSubmit.

      const flexUserUUID = currentUser && currentUser.id && currentUser.id.uuid;
      if (!flexUserUUID) {
        props.history.push('/l/new');
        return;
      }

      try {
        const { data: decision } = await axios.post(
          `${config.listingService}listing-precheck`,
          { flexUserUUID },
          { headers: { 'content-type': 'application/json' } }
        );
        if (decision && decision.allowed === false) {
          setUpgradeDecision(decision);
          return;
        }
      } catch (e) {
        console.log('[listing-precheck] precheck failed, allowing proceed:', e.message || e);
      }

      props.history.push('/l/new');
    } finally {
      setIsCreatingAd(false);
    }
  };

  const activeCnt = getListings('published').length;
  const draftCnt = getListings('draft').length;
  const closedCnt = getListings('closed').length;
  const couponCnt = (coupons || []).length;

  const listingTitleById = id => {
    const match = (listings || []).find(item => item.id.uuid === id);
    return match ? match.attributes.title : 'Deleted ad';
  };

  const getCouponStatus = coupon => {
    if (!coupon.active) return { label: 'Inactive', tone: 'neutral' };
    if (coupon.expiryDate && new Date(coupon.expiryDate).getTime() < Date.now()) {
      return { label: 'Expired', tone: 'danger' };
    }
    if (
      typeof coupon.maxRedemptions === 'number' &&
      coupon.redemptionCount >= coupon.maxRedemptions
    ) {
      return { label: 'Limit reached', tone: 'danger' };
    }
    return { label: 'Active', tone: 'success' };
  };

  const formatDiscount = coupon =>
    coupon.discountType === 'percent'
      ? `${coupon.discountValue}% off`
      : `$${coupon.discountValue} off`;

  const formatExpiry = coupon =>
    coupon.expiryDate
      ? `Expires ${new Date(coupon.expiryDate).toLocaleDateString()}`
      : 'No expiry';

  const formatRedemptions = coupon =>
    typeof coupon.maxRedemptions === 'number'
      ? `${coupon.redemptionCount || 0} / ${coupon.maxRedemptions} used`
      : `${coupon.redemptionCount || 0} used`;

  const renderCoupon = coupon => {
    const status = getCouponStatus(coupon);
    const appliesToLabel = coupon.appliesToAll
      ? 'All ads'
      : (coupon.listingIds || []).map(listingTitleById).join(', ') || 'No ads selected';

    return (
      <div key={coupon.id} className={css.couponRow}>
        <div className={css.couponCodeCol}>
          <span className={css.couponCode}>
            <Tag size={13} />
            {coupon.code}
          </span>
          <span
            className={classnames(
              css.couponStatus,
              status.tone === 'success' && css.couponStatusSuccess,
              status.tone === 'danger' && css.couponStatusDanger,
              status.tone === 'neutral' && css.couponStatusNeutral
            )}
          >
            {status.label}
          </span>
        </div>
        <div className={css.couponDetailsCol}>
          <span className={css.couponDiscount}>{formatDiscount(coupon)}</span>
          <span className={css.couponMeta} title={appliesToLabel}>
            {appliesToLabel}
          </span>
        </div>
        <div className={css.couponFooterCol}>
          <span className={css.couponMeta}>{formatRedemptions(coupon)}</span>
          <span className={css.couponMeta}>{formatExpiry(coupon)}</span>
        </div>
      </div>
    );
  };

  const renderCard = (item, state) => {
    const imgUrl =
      item.images && item.images.length > 0
        ? item.images[0].attributes.variants['landscape-crop'].url
        : noImage;
    const pubData = item.attributes.publicData || {};
    const categories = pubData['type-of-service'] || [];
    const createdAt = item.attributes.createdAt;

    let dashboardEvents = [];
    if (state === 'published') {
      dashboardEvents = [
        {
          name: 'Edit',
          icon: faEdit,
          onClick: () => historuPush(`/l/edit/${item.id.uuid}/edit/gallery`),
          isLeft: true,
        },
        {
          name: 'Share',
          icon: faShare,
          onClick: () => {
            const formattedListingUrl = `/l/${strToSlug(item.attributes.title)}/${item.id.uuid}`;
            props.history.push(formattedListingUrl);
          },
          isLeft: true,
        },
        {
          name: 'Close',
          icon: faLock,
          onClick: () => onCloseListing(new UUID(item.id.uuid)),
          iconOnly: true,
        },
      ];
    } else if (state === 'draft') {
      dashboardEvents = [
        {
          name: 'Edit',
          icon: faEdit,
          onClick: () => historuPush(`/l/edit/${item.id.uuid}/draft/service`),
          isLeft: true,
        },
        {
          name: 'Discard',
          icon: faTrash,
          onClick: () => {
            window.confirm('Are you sure you want to Discard the listing') &&
              onDiscardListing(new UUID(item.id.uuid));
          },
          iconOnly: true,
        },
      ];
    } else if (state === 'closed') {
      dashboardEvents = [
        {
          name: 'Edit',
          icon: faEdit,
          onClick: () => historuPush(`/l/edit/${item.id.uuid}/edit/gallery`),
          isLeft: true,
        },
        {
          name: 'Activate',
          icon: faLockOpen,
          onClick: () => {
            const missing = getMissingRequiredField(item);
            if (missing) {
              notifyError(`Add ${missing.label} before activating this ad.`);
              props.history.push({
                pathname: `/l/edit/${item.id.uuid}/edit/${missing.tab}`,
                state: { focusField: missing.field },
              });
              return;
            }
            onOpenListing(new UUID(item.id.uuid));
          },
          isLeft: true,
        },
      ];
    }

    return (
      <NewListingCard
        key={item.id.uuid}
        price={
          item.attributes.price && item.attributes.price.amount
            ? item.attributes.price.amount / 100
            : ''
        }
        per={item?.attributes?.publicData?.per}
        title={item.attributes.title}
        desc={getDescription(item.attributes.description || '')}
        imgUrl={imgUrl}
        state={state}
        categories={categories}
        createdAt={createdAt}
        dashboardEvents={dashboardEvents}
      />
    );
  };

  return (
    <div className={css.wrapper}>

      <div className={css.pageHeader}>
        <div className={css.pageTitleGroup}>
          <h2 className={css.pageTitle}>Ads</h2>
          <p className={css.pageSubtitle}>Manage your coaching ads and attract new clients.</p>
        </div>
        <div className={css.headerActions}>
          <CreateCouponPopup
            listings={getListings('published')}
            onCreated={onGetAllCoupons}
            trigger={
              <span className={css.couponTooltipWrapper}>
                <button className={css.createCouponBtn}>
                  <Tag size={15} />
                  Create Coupon
                </button>
                <span className={css.couponTooltip} role="tooltip">
                  Create a coupon and share it with your clients — they get a
                  discount when booking from your ads in the marketplace.
                </span>
              </span>
            }
          />
          <button
            className={css.createAdBtn}
            onClick={onAddListingClick}
            disabled={isCreatingAd}
          >
            {isCreatingAd ? (
              <IconSpinner className={css.btnSpinner} />
            ) : (
              <Plus size={15} />
            )}
            {isCreatingAd ? 'Loading…' : 'Create Ad'}
          </button>
        </div>
      </div>

      <div className={css.tabsCard}>
        <Tabs selectedTabClassName={css.tabSelected}>
          <TabList className={css.tablist}>
            <Tab className={classnames(css.tab, 'react-tabs__tab')}>
              Active <span className={css.tabCount}>{activeCnt}</span>
            </Tab>
            <Tab className={classnames(css.tab, 'react-tabs__tab')}>
              Draft <span className={css.tabCount}>{draftCnt}</span>
            </Tab>
            <Tab className={classnames(css.tab, 'react-tabs__tab')}>
              Closed <span className={css.tabCount}>{closedCnt}</span>
            </Tab>
            <Tab className={classnames(css.tab, 'react-tabs__tab')}>
              Coupons <span className={css.tabCount}>{couponCnt}</span>
            </Tab>
          </TabList>

          <div className={css.line} />

          <TabPanel>
            <div className={css.listings}>
              {queryInProgress ? (
                <IconSpinner />
              ) : (
                activeCnt === 0 && (
                  <p className={css.emptyMsg}>
                    You don't have any published listings yet. Please create your listing, so that
                    we can advertise your services and clients can find you on Viveka.
                  </p>
                )
              )}
              {getListings('published').map(item => renderCard(item, 'published'))}
              {!queryInProgress && (
                <div
                  className={css.add_card}
                  onClick={() => onAddListingClick()}
                  title="Add a new ad"
                >
                  <div className={css.add_card_circle}>
                    {isCreatingAd ? (
                      <IconSpinner className={css.addCardSpinner} />
                    ) : (
                      <Plus size={24} />
                    )}
                  </div>
                  <div className={css.add_card_label}>
                    {isCreatingAd ? 'Loading…' : 'Add Ad'}
                  </div>
                  <div className={css.add_card_hint}>Create a new ad to attract new clients</div>
                </div>
              )}
            </div>
          </TabPanel>

          <TabPanel>
            <div className={css.listings}>
              {draftCnt === 0 && <p className={css.emptyMsg}>No listings in draft</p>}
              {getListings('draft').map(item => renderCard(item, 'draft'))}
            </div>
          </TabPanel>

          <TabPanel>
            <div className={css.listings}>
              {closedCnt === 0 && <p className={css.emptyMsg}>No closed listings</p>}
              {getListings('closed').map(item => renderCard(item, 'closed'))}
            </div>
          </TabPanel>

          <TabPanel>
            <div className={css.couponsList}>
              {couponsFetching ? (
                <IconSpinner />
              ) : couponCnt === 0 ? (
                <p className={css.emptyMsg}>
                  You haven't created any coupons yet. Use "Create Coupon" above to give clients
                  a discount code they can apply at checkout.
                </p>
              ) : (
                (coupons || []).map(coupon => renderCoupon(coupon))
              )}
            </div>
          </TabPanel>
        </Tabs>
      </div>

      <UpgradeRequiredModal
        isOpen={!!upgradeDecision}
        onClose={closeUpgradeModal}
        onUpgrade={handleUpgradeClick}
        featureName="create another active listing"
        currentTierName={upgradeDecision && upgradeDecision.tierName}
        requiredTierName={upgradeDecision && upgradeDecision.requiredTierName}
        limit={upgradeDecision && upgradeDecision.limit}
        used={upgradeDecision && upgradeDecision.used}
        reason={upgradeDecision && upgradeDecision.reason}
      />
    </div>
  );
}

const mapStateToProps = state => {
  const { notification } = state.utility;
  const { coupons, couponsFetching } = state.coupons;

  return {
    notification,
    coupons,
    couponsFetching,
  };
};

const mapDispatchToProps = dispatch => ({
  notify: data => {
    dispatch(showNotification(data));
  },

  closeNotification: () => {
    dispatch(closeNotification());
  },

  onGetAllCoupons: () => dispatch(getAllCoupons()),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(DashboardListingWrapper);
